import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import { useDispatch } from 'react-redux';

import Autocomplete from '@gef-ui/form-material/Autocomplete';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { codelistProvider } from 'components';
import DatePickerFullWidth from 'components/atoms/GefMui/DatePickerFullWidth';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import { changeQuickFilter, fetchUnlockedClients, resetFormCriteria } from 'features/views/unlockedClients/actions';
import { FIELDS } from 'features/views/unlockedClients/form';
import { getExactlyXYearsAgoAsDateObj } from 'utils/apiDateTime';

const UnlockedClientsForm = ({ unlockChannels }) => {
	const dispatch = useDispatch();

	const handleResetFormCriteria = useCallback(() => {
		dispatch(resetFormCriteria());
	}, [dispatch]);

	const handleSubmit = useCallback(async () => {
		dispatch(changeQuickFilter(null));
		dispatch(fetchUnlockedClients());
	}, [dispatch]);

	return (
		<Grid container>
			<Grid size={12}>
				<Grid container spacing={2}>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.surname}
							label={getLabelMessage('unlockedClients:surname')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.SURNAME,
								},
							}}
						/>
					</Grid>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.firstName}
							label={getLabelMessage('unlockedClients:firstName')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.NAME,
								},
							}}
						/>
					</Grid>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.businessName}
							label={getLabelMessage('unlockedClients:businessName')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.BUSINESS_NAME,
								},
							}}
						/>
					</Grid>
					<Grid size={3}>
						<DatePickerFullWidth
							fieldName={FIELDS.birthDate}
							label={getLabelMessage('unlockedClients:birthDate')}
						/>
					</Grid>
					<Tooltip title={getLabelMessage('tooltips:birthNumber')}>
						<Grid size={3}>
							<TextField
								size="small"
								fieldName={FIELDS.personIn}
								label={getLabelMessage('unlockedClients:personIn')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.PERSON_IN,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Tooltip title={getLabelMessage('tooltips:taxNumber')}>
						<Grid size={3}>
							<TextField
								size="small"
								fieldName={FIELDS.businessIn}
								label={getLabelMessage('unlockedClients:businessIn')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.TAX_NUMBER,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={6}>
						<Autocomplete
							multiple
							size="small"
							fieldName={FIELDS.unlockChannels}
							label={getLabelMessage('unlockedClients:unlockChannels')}
							options={unlockChannels}
							labelPropName="shortDescription"
						/>
					</Grid>
					<Grid size={3}>
						<DatePickerFullWidth
							fieldName={FIELDS.unlockedFrom}
							label={getLabelMessage('unlockedClients:unlockedFrom')}
							minDate={getExactlyXYearsAgoAsDateObj(-1)}
							disableFuture
						/>
					</Grid>
					<Grid size={3}>
						<DatePickerFullWidth
							fieldName={FIELDS.unlockedTo}
							label={getLabelMessage('unlockedClients:unlockedTo')}
						/>
					</Grid>
					<Grid size={3}>
						<DatePickerFullWidth
							fieldName={FIELDS.extendedFrom}
							label={getLabelMessage('unlockedClients:extendedFrom')}
							minDate={getExactlyXYearsAgoAsDateObj(-1)}
							disableFuture
						/>
					</Grid>
					<Grid size={3}>
						<DatePickerFullWidth
							fieldName={FIELDS.extendedTo}
							label={getLabelMessage('unlockedClients:extendedTo')}
						/>
					</Grid>
					<Grid size={8} />
					<Grid size={2}>
						<Button variant="outlined" onClick={handleResetFormCriteria} fullWidth>
							{getLabelMessage('common:resetButton')}
						</Button>
					</Grid>
					<Grid size={2}>
						<SubmitButton
							variant="contained"
							onClick={handleSubmit}
							fullWidth
							text={getLabelMessage('common:searchButton')}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

UnlockedClientsForm.propTypes = {
	unlockChannels: PropTypes.array,
};

export default codelistProvider(codelists.unlockChannels, 'unlockChannels')(UnlockedClientsForm);
